/**
 * Roster Management Page
 * Upload, view, and manage athlete roster
 */

import { parseCSV, parseCSVWithMapping, getCSVHeaders, getAthleteName, getFieldDisplayName, getAvailableFields } from '../lib/csv.js'
import { loadRoster, clearRoster, getRosterStats, getSelectedRace } from '../lib/storage.js'
import { uploadRosterToCloud } from '../lib/firebase.js'

let currentRoster = []
let filteredRoster = []
let pendingUpload = null
let editingAthlete = null  // Bib of athlete being edited, or 'new' for adding
let searchQuery = ''
let sortField = 'bib'
let sortDirection = 'asc' // or 'desc'
let csvData = null  // Raw CSV data for mapping
let columnMapping = {}  // Current column mapping
let mappingStep = false  // Are we in the column mapping step?

/**
 * Initialize roster page
 */
export function initRosterPage() {
  currentRoster = loadRoster()
}

/**
 * Render the Roster page
 * @param {HTMLElement} container - The container element
 * @param {Function} onNavigate - Callback for navigation
 */
export function renderRosterPage(container, onNavigate) {
  const selectedRace = getSelectedRace()
  currentRoster = loadRoster(selectedRace?.id)

  // Apply search filter
  if (searchQuery) {
    const q = searchQuery.toLowerCase()
    filteredRoster = currentRoster.filter(a =>
      a.bib.toString().includes(q) ||
      a.firstName.toLowerCase().includes(q) ||
      a.lastName.toLowerCase().includes(q) ||
      a.team?.toLowerCase().includes(q)
    )
  } else {
    filteredRoster = currentRoster
  }

  // Apply sorting
  filteredRoster.sort((a, b) => {
    let valA = a[sortField]
    let valB = b[sortField]

    // Handle special cases / nulls
    if (valA === undefined || valA === null) valA = ''
    if (valB === undefined || valB === null) valB = ''

    // Numeric sort for Bib
    if (sortField === 'bib') {
      return sortDirection === 'asc' ? valA - valB : valB - valA
    }

    // String sort for others
    valA = valA.toString().toLowerCase()
    valB = valB.toString().toLowerCase()

    if (valA < valB) return sortDirection === 'asc' ? -1 : 1
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1
    return 0
  })

  const stats = getRosterStats(selectedRace?.id)

  container.innerHTML = `
    <div class="roster-page">
      <header class="page-header">
        <div class="header-main">
          <h1>Roster Management</h1>
          ${selectedRace ? `<div class="current-race-tag">Race: ${selectedRace.name}</div>` : '<div class="current-race-tag warning">No Race Selected</div>'}
        </div>
        <div class="header-actions">
          ${currentRoster.length > 0 ? `
            <button class="btn-secondary btn-small" id="clearRosterBtn">Clear All</button>
          ` : ''}
          <button class="btn-secondary btn-small" id="changeRaceBtn">Switch Race</button>
        </div>
      </header>

      ${currentRoster.length === 0 && !searchQuery ? renderUploadSection() : renderRosterView(stats, selectedRace)}
    </div>
  `

  attachRosterHandlers(container, onNavigate)
}

/**
 * Render upload section when no roster exists
 */
function renderUploadSection() {
  const selectedRace = getSelectedRace()

  return `
    <div class="upload-section">
      ${!selectedRace ? `
        <div class="upload-warning">
          <span class="warning-icon">⚠️</span>
          <div>
            <strong>No race selected</strong>
            <p>Select a race first to link this roster to it, or upload without a race selection.</p>
          </div>
        </div>
      ` : `
        <div class="upload-info">
          <span class="info-icon">📋</span>
          <div>
            <strong>Uploading roster for:</strong>
            <p class="race-name">${selectedRace.name}</p>
          </div>
        </div>
      `}

      <div class="upload-dropzone" id="dropzone">
        <div class="dropzone-content">
          <span class="dropzone-icon">📄</span>
          <h3>Upload Roster CSV</h3>
          <p>Drag and drop a CSV file here, or click to browse</p>
          <p class="text-secondary">Supports: Bib, Name, Gender, Grade, Team</p>
          <input type="file" id="fileInput" accept=".csv,.txt" hidden />
          <button class="btn-primary" id="browseBtn">Browse Files</button>
        </div>
      </div>

      <div class="upload-help">
        <h4>Expected CSV Format</h4>
        <pre class="code-block">Bib,First Name,Last Name,Gender,Grade,Team
301,Colin,Atoule,M,12,Pioneer
302,Yeonu,Bae,M,9,Pioneer</pre>
        <p class="text-secondary">Column names are flexible. We detect common variations like "Race #" for Bib, "First" for First Name, etc.</p>
      </div>
    </div>

    <div id="previewSection" class="preview-section hidden"></div>
  `
}

/**
 * Render roster view when roster exists
 */
function renderRosterView(stats, selectedRace) {
  // Get athlete being edited (if any)
  const editAthlete = editingAthlete === 'new'
    ? { bib: '', firstName: '', lastName: '', gender: '', grade: '', ageGroup: '', division: '', team: '' }
    : editingAthlete
      ? currentRoster.find(a => a.bib === editingAthlete)
      : null

  // Get unique age groups and divisions for autocomplete
  const ageGroups = [...new Set(currentRoster.map(a => a.ageGroup).filter(Boolean))]
  const divisions = [...new Set(currentRoster.map(a => a.division).filter(Boolean))]

  return `
    <div class="roster-toolbar">
      <div class="search-box">
        <span class="search-icon">🔍</span>
        <input type="text" id="rosterSearch" placeholder="Search by name, bib, or team..." value="${searchQuery}" />
      </div>
      <div class="roster-actions">
        <button class="btn-primary" id="addAthleteBtn">+ Add Athlete</button>
        <button class="btn-secondary" id="uploadMoreBtn">Import CSV</button>
      </div>
    </div>

    <div class="roster-stats">
      <div class="stat-card">
        <span class="stat-value">${stats.total}</span>
        <span class="stat-label">Athletes</span>
      </div>
      <div class="stat-card">
        <span class="stat-value">${stats.teams.length}</span>
        <span class="stat-label">Teams</span>
      </div>
    </div>

    <!-- Quick Add/Edit Form -->
    ${editAthlete !== null ? `
      <div class="athlete-form" id="athleteForm">
        <h3>${editingAthlete === 'new' ? 'Add New Athlete' : `Edit Bib #${editingAthlete}`}</h3>
        <div class="form-grid">
          <div class="form-field">
            <label>Bib #</label>
            <input type="number" id="formBib" value="${editAthlete.bib}" placeholder="123" ${editingAthlete !== 'new' ? 'readonly' : ''} />
          </div>
          <div class="form-field">
            <label>First Name</label>
            <input type="text" id="formFirstName" value="${editAthlete.firstName || ''}" placeholder="John" />
          </div>
          <div class="form-field">
            <label>Last Name</label>
            <input type="text" id="formLastName" value="${editAthlete.lastName || ''}" placeholder="Smith" />
          </div>
          <div class="form-field">
            <label>Gender</label>
            <select id="formGender">
              <option value="">-</option>
              <option value="M" ${editAthlete.gender === 'M' ? 'selected' : ''}>Male</option>
              <option value="F" ${editAthlete.gender === 'F' ? 'selected' : ''}>Female</option>
              <option value="NB" ${editAthlete.gender === 'NB' ? 'selected' : ''}>Non-Binary</option>
            </select>
          </div>
          <div class="form-field">
            <label>Grade</label>
            <input type="number" id="formGrade" value="${editAthlete.grade || ''}" placeholder="9-12" min="1" max="12" />
          </div>
          <div class="form-field">
            <label>Age Group</label>
            <input type="text" id="formAgeGroup" value="${editAthlete.ageGroup || ''}" placeholder="e.g. 14-15" list="ageGroupsList" />
            <datalist id="ageGroupsList">
              ${ageGroups.map(ag => `<option value="${ag}">`).join('')}
            </datalist>
          </div>
          <div class="form-field">
            <label>Division</label>
            <input type="text" id="formDivision" value="${editAthlete.division || ''}" placeholder="e.g. Varsity" list="divisionsList" />
            <datalist id="divisionsList">
              ${divisions.map(d => `<option value="${d}">`).join('')}
            </datalist>
          </div>
          <div class="form-field">
            <label>Team</label>
            <input type="text" id="formTeam" value="${editAthlete.team || ''}" placeholder="School Name" list="teamsList" />
            <datalist id="teamsList">
              ${stats.teams.map(t => `<option value="${t}">`).join('')}
            </datalist>
          </div>
        </div>
        <div class="form-actions">
          <button class="btn-secondary" id="cancelFormBtn">Cancel</button>
          <button class="btn-primary" id="saveFormBtn">${editingAthlete === 'new' ? 'Add Athlete' : 'Save Changes'}</button>
        </div>
      </div>
    ` : ''}

    <div class="roster-table-container">
      <table class="roster-table">
        <thead>
          <tr>
            <th class="sortable-header" data-sort="bib">
              Bib ${sortField === 'bib' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th class="sortable-header" data-sort="firstName">
              First Name ${sortField === 'firstName' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th class="sortable-header" data-sort="lastName">
              Last Name ${sortField === 'lastName' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th>G</th>
            <th>Gr</th>
            <th class="sortable-header" data-sort="team">
              Team ${sortField === 'team' ? (sortDirection === 'asc' ? '▲' : '▼') : ''}
            </th>
            <th class="actions-col"></th>
          </tr>
        </thead>
        <tbody>
          ${filteredRoster.map(athlete => `
            <tr class="${editingAthlete === athlete.bib ? 'editing' : ''}">
              <td class="bib-cell">${athlete.bib}</td>
              <td class="name-cell">${athlete.firstName || '-'}</td>
              <td class="name-cell">${athlete.lastName || '-'}</td>
              <td>${athlete.gender || '-'}</td>
              <td>${athlete.grade || '-'}</td>
              <td>${athlete.team || '-'}</td>
              <td class="actions-col">
                <button class="btn-icon btn-edit-athlete" data-bib="${athlete.bib}" title="Edit">✎</button>
                <button class="btn-icon btn-delete-athlete" data-bib="${athlete.bib}" title="Delete">×</button>
              </td>
            </tr>
          `).join('')}
          ${filteredRoster.length === 0 && searchQuery ? '<tr><td colspan="7" class="no-results">No athletes match your search</td></tr>' : ''}
        </tbody>
      </table>
    </div>
  `
}

/**
 * Render upload preview
 */
function renderPreview(result) {
  const previewSection = document.getElementById('previewSection')
  if (!previewSection) return

  const hasErrors = result.errors.length > 0
  const hasWarnings = result.warnings.length > 0

  previewSection.classList.remove('hidden')
  previewSection.innerHTML = `
    <h3>Upload Preview</h3>

    ${hasErrors ? `
      <div class="message message-error">
        <strong>Errors:</strong>
        <ul>
          ${result.errors.map(e => `<li>${e}</li>`).join('')}
        </ul>
      </div>
    ` : ''}

    ${hasWarnings ? `
      <div class="message message-warning">
        <strong>Warnings:</strong>
        <ul>
          ${result.warnings.map(w => `<li>${w}</li>`).join('')}
        </ul>
      </div>
    ` : ''}

    ${result.athletes.length > 0 ? `
      <div class="preview-summary">
        <p><strong>${result.athletes.length}</strong> athletes found</p>
        <p class="text-secondary">
          ${result.athletes.filter(a => a.gender === 'M').length} male,
          ${result.athletes.filter(a => a.gender === 'F').length} female
        </p>
      </div>

      <div class="preview-table-container">
        <table class="roster-table preview-table">
          <thead>
            <tr>
              <th>Bib</th>
              <th>Name</th>
              <th>Gender</th>
              <th>Grade</th>
              <th>Team</th>
            </tr>
          </thead>
          <tbody>
            ${result.athletes.map(athlete => `
              <tr>
                <td class="bib-cell">${athlete.bib}</td>
                <td>${getAthleteName(athlete)}</td>
                <td>${athlete.gender || '-'}</td>
                <td>${athlete.grade || '-'}</td>
                <td>${athlete.team || '-'}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
      </div>

      <div class="preview-actions">
        <button class="btn-secondary" id="cancelUploadBtn">Cancel</button>
        <button class="btn-primary" id="confirmUploadBtn" ${hasErrors ? 'disabled' : ''}>
          Save ${result.athletes.length} Athletes
        </button>
      </div>
    ` : ''}
  `

  // Attach preview handlers
  const cancelBtn = document.getElementById('cancelUploadBtn')
  const confirmBtn = document.getElementById('confirmUploadBtn')

  if (cancelBtn) {
    cancelBtn.addEventListener('click', () => {
      pendingUpload = null
      previewSection.classList.add('hidden')
      previewSection.innerHTML = ''
    })
  }

  if (confirmBtn && !hasErrors) {
    confirmBtn.addEventListener('click', async () => {
      const selectedRace = getSelectedRace()
      if (!selectedRace?.id) {
        alert('Please select a race first')
        return
      }

      const count = pendingUpload.athletes.length
      confirmBtn.disabled = true
      confirmBtn.textContent = 'Uploading...'

      // Upload directly to Firebase
      const result = await uploadRosterToCloud(selectedRace.id, pendingUpload.athletes)

      if (result.error) {
        alert('Failed to upload roster: ' + result.error)
        confirmBtn.disabled = false
        confirmBtn.textContent = 'Confirm Import'
        return
      }

      currentRoster = pendingUpload.athletes
      pendingUpload = null
      csvData = null
      columnMapping = {}
      mappingStep = false

      // Show success toast
      if (window.showToast) {
        window.showToast(`Roster uploaded: ${count} athletes`, 'success')
      }

      // Re-render page after delay for subscription to update
      const pageContainer = document.querySelector('.roster-page').parentElement
      setTimeout(() => renderRosterPage(pageContainer), 500)
    })
  }
}

/**
 * Attach event handlers
 */
function attachRosterHandlers(container, onNavigate) {
  const dropzone = document.getElementById('dropzone')
  const fileInput = document.getElementById('fileInput')
  const browseBtn = document.getElementById('browseBtn')
  const clearRosterBtn = document.getElementById('clearRosterBtn')
  const changeRaceBtn = document.getElementById('changeRaceBtn')
  const rosterSearch = document.getElementById('rosterSearch')
  const addAthleteBtn = document.getElementById('addAthleteBtn')
  const cancelFormBtn = document.getElementById('cancelFormBtn')
  const saveFormBtn = document.getElementById('saveFormBtn')
  const uploadMoreBtn = document.getElementById('uploadMoreBtn')

  // Sort headers
  container.querySelectorAll('.sortable-header').forEach(th => {
    th.addEventListener('click', () => {
      const field = th.dataset.sort
      if (sortField === field) {
        sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        sortField = field
        sortDirection = 'asc'
      }
      renderRosterPage(container, onNavigate)
    })
  })

  // Search input
  if (rosterSearch) {
    rosterSearch.addEventListener('input', (e) => {
      searchQuery = e.target.value
      renderRosterPage(container, onNavigate)
      // Re-focus search input
      document.getElementById('rosterSearch')?.focus()
      const val = document.getElementById('rosterSearch').value
      document.getElementById('rosterSearch').setSelectionRange(val.length, val.length)
    })
  }

  // Change race button
  if (changeRaceBtn) {
    changeRaceBtn.addEventListener('click', () => {
      onNavigate('races')
    })
  }

  // Add athlete button
  if (addAthleteBtn) {
    addAthleteBtn.addEventListener('click', () => {
      editingAthlete = 'new'
      renderRosterPage(container, onNavigate)
      // Focus on bib input
      setTimeout(() => document.getElementById('formBib')?.focus(), 50)
    })
  }

  // Cancel form button
  if (cancelFormBtn) {
    cancelFormBtn.addEventListener('click', () => {
      editingAthlete = null
      renderRosterPage(container, onNavigate)
    })
  }

  // Save form button
  if (saveFormBtn) {
    saveFormBtn.addEventListener('click', () => {
      handleSaveAthlete(container, onNavigate)
    })
  }

  // Edit athlete buttons
  container.querySelectorAll('.btn-edit-athlete').forEach(btn => {
    btn.addEventListener('click', () => {
      const bib = parseInt(btn.dataset.bib, 10)
      editingAthlete = bib
      renderRosterPage(container, onNavigate)
      setTimeout(() => document.getElementById('formFirstName')?.focus(), 50)
    })
  })

  // Delete athlete buttons
  container.querySelectorAll('.btn-delete-athlete').forEach(btn => {
    btn.addEventListener('click', async () => {
      const bib = parseInt(btn.dataset.bib, 10)
      const athlete = currentRoster.find(a => a.bib === bib)
      const selectedRace = getSelectedRace()
      if (athlete && selectedRace?.id && confirm(`Delete ${getAthleteName(athlete)} (Bib #${bib})?`)) {
        currentRoster = currentRoster.filter(a => a.bib !== bib)
        await uploadRosterToCloud(selectedRace.id, currentRoster)
        renderRosterPage(container, onNavigate)
      }
    })
  })

  // Form enter key to save
  const formInputs = container.querySelectorAll('#athleteForm input, #athleteForm select')
  formInputs.forEach(input => {
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        handleSaveAthlete(container, onNavigate)
      } else if (e.key === 'Escape') {
        editingAthlete = null
        renderRosterPage(container, onNavigate)
      }
    })
  })

  // File input change
  if (fileInput) {
    fileInput.addEventListener('change', (e) => {
      const file = e.target.files[0]
      if (file) handleFileUpload(file)
    })
  }

  // Browse button
  if (browseBtn) {
    browseBtn.addEventListener('click', () => {
      fileInput?.click()
    })
  }

  // Drag and drop
  if (dropzone) {
    dropzone.addEventListener('dragover', (e) => {
      e.preventDefault()
      dropzone.classList.add('dragover')
    })

    dropzone.addEventListener('dragleave', () => {
      dropzone.classList.remove('dragover')
    })

    dropzone.addEventListener('drop', (e) => {
      e.preventDefault()
      dropzone.classList.remove('dragover')
      const file = e.dataTransfer.files[0]
      if (file) handleFileUpload(file)
    })

    // Click to browse
    dropzone.addEventListener('click', (e) => {
      if (e.target !== browseBtn && !browseBtn?.contains(e.target)) {
        fileInput?.click()
      }
    })
  }

  // Clear roster
  if (clearRosterBtn) {
    clearRosterBtn.addEventListener('click', () => {
      if (confirm('Clear all roster data? This cannot be undone.')) {
        clearRoster()
        currentRoster = []
        renderRosterPage(container, onNavigate)
      }
    })
  }

  // Upload more (replace roster)
  if (uploadMoreBtn) {
    uploadMoreBtn.addEventListener('click', () => {
      // Show upload UI
      container.innerHTML = ''
      container.innerHTML = `
        <div class="roster-page">
          <header class="page-header">
            <h1>Upload New Roster</h1>
            <button class="btn-secondary btn-small" id="cancelUploadPageBtn">Cancel</button>
          </header>
          ${renderUploadSection()}
        </div>
      `
      attachRosterHandlers(container, onNavigate)

      document.getElementById('cancelUploadPageBtn')?.addEventListener('click', () => {
        renderRosterPage(container, onNavigate)
      })
    })
  }
}

/**
 * Handle save athlete (add or edit)
 */
async function handleSaveAthlete(container, onNavigate) {
  const bibInput = document.getElementById('formBib')
  const firstNameInput = document.getElementById('formFirstName')
  const lastNameInput = document.getElementById('formLastName')
  const genderSelect = document.getElementById('formGender')
  const gradeInput = document.getElementById('formGrade')
  const ageGroupInput = document.getElementById('formAgeGroup')
  const divisionInput = document.getElementById('formDivision')
  const teamInput = document.getElementById('formTeam')

  const bib = parseInt(bibInput?.value, 10)
  const firstName = firstNameInput?.value.trim() || ''
  const lastName = lastNameInput?.value.trim() || ''
  const gender = genderSelect?.value || null
  const grade = gradeInput?.value ? parseInt(gradeInput.value, 10) : null
  const ageGroup = ageGroupInput?.value.trim() || null
  const division = divisionInput?.value.trim() || null
  const team = teamInput?.value.trim() || null

  // Validation
  if (!bib || isNaN(bib)) {
    alert('Please enter a valid bib number')
    bibInput?.focus()
    return
  }

  if (!firstName && !lastName) {
    alert('Please enter at least a first or last name')
    firstNameInput?.focus()
    return
  }

  // Check for duplicate bib when adding new
  if (editingAthlete === 'new' && currentRoster.some(a => a.bib === bib)) {
    alert(`Bib #${bib} already exists in the roster`)
    bibInput?.focus()
    return
  }

  const athlete = {
    bib,
    firstName,
    lastName,
    gender,
    grade,
    ageGroup,
    division,
    team
  }

  if (editingAthlete === 'new') {
    // Add new athlete
    currentRoster.push(athlete)
    // Sort by bib number
    currentRoster.sort((a, b) => a.bib - b.bib)
  } else {
    // Update existing athlete
    const index = currentRoster.findIndex(a => a.bib === editingAthlete)
    if (index !== -1) {
      currentRoster[index] = athlete
    }
  }

  const selectedRace = getSelectedRace()
  if (selectedRace?.id) {
    await uploadRosterToCloud(selectedRace.id, currentRoster)
  }
  editingAthlete = null
  renderRosterPage(container, onNavigate)
}

/**
 * Handle file upload - shows column mapping UI first
 */
function handleFileUpload(file) {
  if (!file.name.match(/\.(csv|txt)$/i)) {
    alert('Please upload a CSV file')
    return
  }

  const reader = new FileReader()
  reader.onload = (e) => {
    const csvText = e.target.result

    // Get headers and sample rows for mapping UI
    csvData = getCSVHeaders(csvText)
    csvData.rawText = csvText  // Store raw text for later parsing

    // Initialize column mapping from auto-detected values
    columnMapping = { ...csvData.detectedMapping }
    mappingStep = true

    // Render the column mapping UI
    renderColumnMapping()
  }
  reader.readAsText(file)
}

/**
 * Render column mapping UI
 */
function renderColumnMapping() {
  const previewSection = document.getElementById('previewSection')
  if (!previewSection) return

  const fields = getAvailableFields()
  const requiredFields = ['bib', 'firstName', 'lastName']

  previewSection.classList.remove('hidden')
  previewSection.innerHTML = `
    <div class="mapping-section">
      <h3>Map CSV Columns</h3>
      <p class="text-secondary">Match your CSV columns to athlete fields. We've auto-detected what we can.</p>

      <div class="mapping-grid">
        ${fields.map(field => {
    const isRequired = field === 'bib' || (field === 'firstName' && columnMapping.lastName === -1) || (field === 'lastName' && columnMapping.firstName === -1)
    const currentIndex = columnMapping[field]

    return `
            <div class="mapping-row ${isRequired ? 'required' : ''}">
              <label class="mapping-label">
                ${getFieldDisplayName(field)}
                ${isRequired ? '<span class="required-star">*</span>' : ''}
              </label>
              <select class="mapping-select" data-field="${field}">
                <option value="-1">-- Not Mapped --</option>
                ${csvData.headers.map((header, idx) => `
                  <option value="${idx}" ${currentIndex === idx ? 'selected' : ''}>${header}</option>
                `).join('')}
              </select>
              ${currentIndex >= 0 && csvData.sampleRows[0] ? `
                <span class="sample-value">${csvData.sampleRows[0][currentIndex] || '-'}</span>
              ` : '<span class="sample-value">-</span>'}
            </div>
          `
  }).join('')}
      </div>

      <div class="sample-data">
        <h4>Sample Data (first 3 rows)</h4>
        <div class="sample-table-container">
          <table class="sample-table">
            <thead>
              <tr>
                ${csvData.headers.map(h => `<th>${h}</th>`).join('')}
              </tr>
            </thead>
            <tbody>
              ${csvData.sampleRows.slice(0, 3).map(row => `
                <tr>
                  ${row.map(cell => `<td>${cell || '-'}</td>`).join('')}
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>

      <div class="mapping-actions">
        <button class="btn-secondary" id="cancelMappingBtn">Cancel</button>
        <button class="btn-primary" id="confirmMappingBtn">Preview Import</button>
      </div>
    </div>
  `

  attachMappingHandlers()
}

/**
 * Attach handlers for column mapping UI
 */
function attachMappingHandlers() {
  // Column mapping dropdowns
  document.querySelectorAll('.mapping-select').forEach(select => {
    select.addEventListener('change', (e) => {
      const field = e.target.dataset.field
      const value = parseInt(e.target.value, 10)
      columnMapping[field] = value

      // Update sample value display
      const row = e.target.closest('.mapping-row')
      const sampleSpan = row.querySelector('.sample-value')
      if (sampleSpan && csvData.sampleRows[0]) {
        sampleSpan.textContent = value >= 0 ? (csvData.sampleRows[0][value] || '-') : '-'
      }
    })
  })

  // Cancel button
  document.getElementById('cancelMappingBtn')?.addEventListener('click', () => {
    csvData = null
    columnMapping = {}
    mappingStep = false
    const previewSection = document.getElementById('previewSection')
    if (previewSection) {
      previewSection.classList.add('hidden')
      previewSection.innerHTML = ''
    }
  })

  // Confirm button - parse with mapping and show preview
  document.getElementById('confirmMappingBtn')?.addEventListener('click', () => {
    // Validate required fields
    if (columnMapping.bib === -1 || columnMapping.bib === undefined) {
      alert('Please map a column to Bib #')
      return
    }

    if ((columnMapping.firstName === -1 || columnMapping.firstName === undefined) &&
      (columnMapping.lastName === -1 || columnMapping.lastName === undefined)) {
      alert('Please map at least a First Name or Last Name column')
      return
    }

    // Parse CSV with custom mapping
    const result = parseCSVWithMapping(csvData.rawText, columnMapping)

    pendingUpload = result
    mappingStep = false
    renderPreview(result)
  })
}

